import React, { useEffect, useRef, useState } from 'react';
import { Avatar, Button, Dropdown, Input, Skeleton, Space, Tooltip, message } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faCloudCheck, faEye, faLink, faPen, faShareNodes, faStar, faUserGear } from '@fortawesome/pro-solid-svg-icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getNote, setNoteFavorite, updateNote } from '../../services/notes';
import RichNoteEditor from './RichNoteEditor';
import NoteAccessDrawer from './NoteAccessDrawer';
import { useNoteCollaboration } from '../../hooks/useNoteCollaboration';
import { useAuthStore } from '../../store/authStore';
import { noteUrl } from './noteUrls';

interface Props {
  noteId: string | null;
  onClose: () => void;
  editing: boolean;
  onEditingChange: (editing: boolean) => void;
  seedImportedContent?: boolean;
  onImportedContentSeeded?: () => void;
}

const errorMessage = (error: any, fallback: string) => error.response?.data?.error || error.message || fallback;

const NoteView: React.FC<Props> = ({ noteId, onClose, editing, onEditingChange, seedImportedContent = false, onImportedContentSeeded }) => {
  const queryClient = useQueryClient();
  const currentUser = useAuthStore(state => state.user);
  const [title, setTitle] = useState('');
  const [metadataSaving, setMetadataSaving] = useState(false);
  const [sharingOpen, setSharingOpen] = useState(false);
  const initializedNoteId = useRef<string | null>(null);
  const titleBaseline = useRef('');
  const saveSequence = useRef(0);

  const { data: note, isLoading } = useQuery({
    queryKey: ['note', noteId],
    queryFn: () => getNote(noteId!),
    enabled: Boolean(noteId),
  });
  const collaboration = useNoteCollaboration(note?.id || null, Boolean(note));

  useEffect(() => {
    if (!note || initializedNoteId.current === note.id) return;
    initializedNoteId.current = note.id;
    setTitle(note.title);
    titleBaseline.current = note.title;
  }, [note]);

  useEffect(() => {
    if (!collaboration.accessRevoked) return;
    message.warning('Your access to this note was removed');
    queryClient.invalidateQueries({ queryKey: ['notes'] });
    onClose();
  }, [collaboration.accessRevoked, onClose, queryClient]);

  const canEdit = note?.currentPermission === 'OWNER' || note?.currentPermission === 'MANAGER' || note?.currentPermission === 'EDITOR';
  const canManage = note?.currentPermission === 'OWNER' || note?.currentPermission === 'MANAGER';

  useEffect(() => {
    if (!note || !canEdit || !editing || title === titleBaseline.current) return;
    const sequence = ++saveSequence.current;
    setMetadataSaving(true);
    const timer = setTimeout(async () => {
      try {
        const updated = await updateNote(note.id, { title });
        if (sequence === saveSequence.current) {
          titleBaseline.current = updated.title;
          if (updated.title !== title) setTitle(updated.title);
          queryClient.setQueryData(['note', noteId], updated);
          queryClient.setQueryData(['note', updated.id], updated);
          queryClient.invalidateQueries({ queryKey: ['notes'] });
        }
      } catch (error) {
        if (sequence === saveSequence.current) message.error(errorMessage(error, 'Autosave failed'));
      } finally {
        if (sequence === saveSequence.current) setMetadataSaving(false);
      }
    }, 650);
    return () => clearTimeout(timer);
  }, [canEdit, editing, note, noteId, queryClient, title]);

  const favoriteMutation = useMutation({
    mutationFn: () => setNoteFavorite(note!.id, !note!.isFavorite),
    onSuccess: () => {
      queryClient.setQueryData(['note', note!.id], { ...note!, isFavorite: !note!.isFavorite });
      queryClient.invalidateQueries({ queryKey: ['notes'] });
    },
    onError: error => message.error(errorMessage(error, 'Could not update favorite')),
  });

  const isSaving = metadataSaving || collaboration.saveStatus === 'saving';
  const saved = !isSaving && collaboration.connected && collaboration.saveStatus === 'saved';
  const saveLabel = isSaving ? 'Saving…' : saved ? 'Saved' : 'Not saved';
  const currentUserColor = collaboration.collaborators.find(person => person.userId === currentUser?.id)?.color || '#d54b9f';

  const copyNoteLink = async () => {
    try {
      if (!note) throw new Error('Note is unavailable');
      await navigator.clipboard.writeText(noteUrl(note));
      message.success('Link copied');
    } catch {
      message.error('Could not copy link');
    }
  };

  return (
    <>
      {noteId && (
        <section className="library-item-view">
          {isLoading ? (
            <div className="library-detail-loading"><Skeleton active paragraph={{ rows: 9 }} /></div>
          ) : note && (
            <div className="library-item-detail">
              <header className="library-detail-header">
                <div className="library-detail-header-left">
                  <Tooltip title="Back to Notes"><Button aria-label="Back to Notes" type="text" className="library-back-button" icon={<FontAwesomeIcon icon={faArrowLeft} />} onClick={onClose} /></Tooltip>
                  <div className="library-header-document-title">
                    {canEdit && editing ? (
                      <Input variant="borderless" value={title} maxLength={180} autoFocus onFocus={event => { if (title === 'Untitled') event.currentTarget.select(); }} onChange={event => setTitle(event.target.value)} />
                    ) : <strong>{title || note.title}</strong>}
                    {canEdit && <span className={`library-header-save-state ${saved ? 'is-saved' : ''} ${!saved && !isSaving ? 'is-unsaved' : ''}`}><FontAwesomeIcon icon={faCloudCheck} />{saveLabel}</span>}
                  </div>
                </div>
                <Space>
                  {collaboration.synced && (
                    <div className="library-presence">
                      <Avatar.Group max={{ count: 3 }} size="small">
                        {collaboration.collaborators.map(person => (
                          <Tooltip title={person.name} key={person.socketId}><Avatar src={person.photoUrl || undefined} style={{ background: person.color }}>{person.name[0]}</Avatar></Tooltip>
                        ))}
                      </Avatar.Group>
                    </div>
                  )}
                  <Tooltip title={note.isFavorite ? 'Remove from favorites' : 'Add to favorites'}>
                    <Button aria-label={note.isFavorite ? 'Remove from favorites' : 'Add to favorites'} type="text" icon={<FontAwesomeIcon icon={faStar} />} className={note.isFavorite ? 'is-favorite' : ''} onClick={() => favoriteMutation.mutate()} />
                  </Tooltip>
                  {canEdit && (
                    <div className="library-mode-toggle" role="group" aria-label="Document mode">
                      <Tooltip title="View"><button aria-label="View note" className={!editing ? 'is-active' : ''} onClick={() => onEditingChange(false)}><FontAwesomeIcon icon={faEye} /></button></Tooltip>
                      <Tooltip title="Edit"><button aria-label="Edit note" className={editing ? 'is-active' : ''} onClick={() => onEditingChange(true)}><FontAwesomeIcon icon={faPen} /></button></Tooltip>
                    </div>
                  )}
                  {canManage && (
                    <Dropdown
                      trigger={['click']}
                      placement="bottomRight"
                      menu={{
                        items: [
                          { key: 'copy-link', label: 'Copy link', icon: <FontAwesomeIcon icon={faLink} /> },
                          { key: 'manage-access', label: 'Manage access', icon: <FontAwesomeIcon icon={faUserGear} /> },
                        ],
                        onClick: ({ key }) => key === 'copy-link' ? copyNoteLink() : setSharingOpen(true),
                      }}
                    >
                      <Tooltip title="Share"><Button aria-label="Share" type="text" icon={<FontAwesomeIcon icon={faShareNodes} />} /></Tooltip>
                    </Dropdown>
                  )}
                </Space>
              </header>

              <main className="library-detail-main library-detail-main--note">
                {collaboration.synced && collaboration.doc && collaboration.provider && (
                  <RichNoteEditor
                    noteId={note.id}
                    doc={collaboration.doc}
                    provider={collaboration.provider}
                    editable={Boolean(editing && canEdit && collaboration.canEdit)}
                    user={{ name: currentUser?.name || 'Collaborator', color: currentUserColor }}
                    legacyContent={Array.isArray(note.content) ? note.content : []}
                    knownSecretBlockIds={(note.secretBlocks || []).map(secretBlock => secretBlock.blockId)}
                    seedImportedContent={seedImportedContent}
                    onImportedContentSeeded={onImportedContentSeeded}
                  />
                )}
              </main>
            </div>
          )}
        </section>
      )}
      <NoteAccessDrawer note={note} open={sharingOpen} onClose={() => setSharingOpen(false)} />
    </>
  );
};

export default NoteView;
